import type { ReactElement } from "react";
import { useTranslation } from "react-i18next";
import type { PeriodSummaryMode } from "@/pages/human-resources/time-management/business/analysis/PeriodSummaryMode.ts";
import { usePeriodSummaryDescription } from "@/pages/human-resources/time-management/schedules/site-schedule/analysis/period-summary/usePeriodSummaryDescription.ts";
import IconWatch from "@/shared/components/generic/icons/IconWatch.tsx";
import IconGraph from "@/shared/components/generic/icons/IconGraph.tsx";
import IconCalendarLeftArrow from "@/shared/components/generic/icons/IconCalendarLeftArrow.tsx";
import ToggleButtonGroup from "@/shared/components/generic/button/toggle-button/ToggleButtonGroup.tsx";
import ToggleButton from "@/shared/components/generic/button/toggle-button/ToggleButton.tsx";
import "./SwitchPeriodSummaryMode.scss";
import type { PeriodSummaryRange } from "@/pages/human-resources/time-management/business/analysis/PeriodSummaryRange.ts";

interface Props {
  mode: PeriodSummaryMode;
  onChange: (value: PeriodSummaryMode) => void;
  date: Date;
  range: PeriodSummaryRange;
}

export default function SwitchPeriodSummaryMode({
  mode,
  onChange,
  date,
  range,
}: Props): ReactElement {
  const { t } = useTranslation("humanResources");
  const description = usePeriodSummaryDescription(mode, date, range);

  const handleChange = (newMode: PeriodSummaryMode) => (): void => {
    if (newMode !== mode) onChange(newMode);
  };

  return (
    <div className="switch-day-summary-mode">
      <ToggleButtonGroup direction="horizontal">
        <ToggleButton
          text={t("timeManagement.analysis.daySummaryMode.FORECAST")}
          icon={IconWatch}
          selected={mode === "FORECAST"}
          onClick={handleChange("FORECAST")}
          outlined
        />
        <ToggleButton
          text={t("timeManagement.analysis.daySummaryMode.PREVIOUS_YEAR")}
          icon={IconCalendarLeftArrow}
          selected={mode === "PREVIOUS_YEAR"}
          onClick={handleChange("PREVIOUS_YEAR")}
          outlined
        />
        <ToggleButton
          text={t("timeManagement.analysis.daySummaryMode.TREND")}
          icon={IconGraph}
          selected={mode === "TREND"}
          onClick={handleChange("TREND")}
          outlined
        />
      </ToggleButtonGroup>
      <div className="switch-description">{description}</div>
    </div>
  );
}
